import React, { useContext } from 'react';
import styled from 'styled-components';
import { MetricKind } from '@/webapi/models';
import { Experience, useExperienceApi } from '@/webapi/use-experience-api';
import { SummaryContext } from '@/features/details/summary/context';
import { selectedGoal } from '@/features/details/charts/more-metrics';
import { formatEnum } from '@/utils/types';
import {
  GoalChip,
  GoalsWrapper,
} from '@/features/editor/widgets/shared/inline-variants-layout/goals';

export function SeeMoreReportsList({
  metricKinds,
  experience,
  setExperience,
}: {
  metricKinds: MetricKind[];
  experience?: Experience;
  setExperience?: (e: Experience) => void;
}) {
  const { upsertExperience, loading } = useExperienceApi(
    experience.lastDevicePreview,
    experience,
  );

  const { getCustomMetricName } = useContext(SummaryContext);
  return (
    <MetricsList style={{ opacity: loading ? 0.15 : 1 }}>
      {metricKinds.map((metric) => {
        const onClick = async () => {
          if (loading || metric === experience.primaryGoal) {
            return;
          }
          const cloned = JSON.parse(JSON.stringify(experience)) as Experience;
          cloned.goals.push(metric);
          setExperience(cloned);
          await upsertExperience(cloned);
        };
        return !selectedGoal(experience, metric) ? (
          <Chip
            focused={selectedGoal(experience, metric)}
            key={metric}
            onClick={onClick}
          >
            {getCustomMetricName(metric) || formatEnum(metric, true)}
          </Chip>
        ) : null;
      })}
    </MetricsList>
  );
}

const Chip = styled(GoalChip)`
  && {
    background-color: #eff0f3;
    color: #0c0c0c;
    margin: 0 !important;
    font-weight: 600;
    font-size: 1rem;
    border: none;
    opacity: 1;
    text-transform: uppercase;
  }

  &&:hover {
    background-color: #e0e0e5;
  }
`;

const MetricsList = styled(GoalsWrapper)`
  && {
    overflow: scroll;
    max-height: calc(100% - 35px);
    margin: 0 auto;
    gap: 1.2rem 1rem;
    padding: 0 0 2rem 0;
    max-width: 100%;
    flex-wrap: wrap;
    justify-content: start;
    align-items: center;
  }
`;

export function MoreMetricsList({
  moreMetrics,
  hasOneVariant,
  experience,
  addMoreMetrics,
  setExperience,
}: any) {
  const shouldShow =
    moreMetrics?.length <= (hasOneVariant ? 6 : 5) &&
    addMoreMetrics?.length > 0;
  if (!shouldShow) {
    return null;
  }
  return (
    shouldShow && (
      <AddMoreWrapper
        style={{
          width: `calc((100% - 70px + 2rem)*(${
            (hasOneVariant ? 8 : 7) - moreMetrics.length
          }/8) - 2rem)`,
        }}
      >
        <Title>Would like to see more reports?</Title>
        <SeeMoreReportsList
          experience={experience}
          metricKinds={addMoreMetrics}
          setExperience={setExperience}
        />
      </AddMoreWrapper>
    )
  );
}

const AddMoreWrapper = styled.div`
  position: absolute;
  height: 100%;
  right: 35px;
  padding-top: 35px;
`;

const Title = styled.p`
  color: #8fa3ad;
  font-size: 1.2rem;
  font-weight: 500;
  font-family: 'Inter', serif;
  margin: 0 0 20px;
`;
